import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichText } from '../../components/RichText';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { LoadingIndicator } from '../../components/Spinner';
import { formatDateTime } from '../../i18n';
import { useMyTicket, useMyTicketArticles, useReplyToMyTicket } from './queries';

/**
 * Ein Vorgang im Portal -- F4-18, F4-23.
 *
 * **Der Verlauf enthaelt nur oeffentliche Beitraege.** Nicht, weil hier
 * gefiltert wuerde, sondern weil der Server nichts anderes herausgibt
 * (`tickets/articles/service.py`). docs/07, Abschnitt 7 fuehrt "interne
 * Notizen im Portal sichtbar" als wiederkehrende Fehlerquelle -- ein Filter
 * an dieser Stelle waere genau die Konstruktion, die diesen Fehler moeglich
 * macht.
 *
 * **Der Aufbau ist: Sachverhalt, Antwortfeld, Verlauf mit dem neuesten
 * Beitrag zuerst.** Das Antwortfeld stand einmal unter dem Verlauf. Bei
 * einem Vorgang mit dreissig Beitraegen hiess das: erst durch den ganzen
 * bisherigen Schriftwechsel scrollen, um antworten zu koennen -- und der
 * letzte Stand, den man dabei sucht, stand am selben Ende. Beides steht
 * jetzt oben.
 */
export function PortalTicketDetail({ ticketId, onBack }: { ticketId: string; onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const ticket = useMyTicket(ticketId);
  const articles = useMyTicketArticles(ticketId);
  const reply = useReplyToMyTicket(ticketId);

  const [message, setMessage] = useState('');

  if (ticket.isPending) return <LoadingIndicator />;
  if (ticket.isError) return <ErrorNotice error={ticket.error} />;

  return (
    <>
      <PageHeader
        title={
          <>
            {ticket.data.title}{' '}
            <Badge tone="info" dot>
              {ticket.data.state.name}
            </Badge>
          </>
        }
        subtitle={ticket.data.ticket_number}
        action={
          <Button variant="ghost" onClick={onBack}>
            {t('portal.backToList')}
          </Button>
        }
      />

      <section className="card">
        <h2>{t('portal.description')}</h2>
        {/* Wie in der Bearbeiteransicht: Auszeichnung, wenn es welche
            gibt -- sonst der Klartext. Eine Anfrage aus dem Portal selbst
            traegt keine; dort laedt niemand einen Editor, um eine Stoerung
            zu melden. */}
        {ticket.data.description_html ? (
          <RichText html={ticket.data.description_html} />
        ) : (
          <p className="prose">{ticket.data.description || '—'}</p>
        )}
      </section>

      <section className="card">
        <h2>{t('portal.conversation')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="portal-reply">
            {t('portal.reply')}
          </label>
          <textarea
            id="portal-reply"
            className="field__control field__control--area"
            rows={4}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
            }}
          />
        </div>

        {reply.isError ? <ErrorNotice error={reply.error} /> : null}

        <Button
          variant="primary"
          disabled={!message.trim()}
          busy={reply.isPending}
          onClick={() => {
            // Als Absatz und nicht als Rohtext: Der Server entschaerft das
            // HTML, aber Zeilenumbrueche gingen ohne Auszeichnung verloren.
            reply.mutate(`<p>${escapeHtml(message)}</p>`, {
              onSuccess: () => {
                setMessage('');
              },
            });
          }}
        >
          {t('portal.send')}
        </Button>

        {articles.isPending ? <LoadingIndicator /> : null}
        {articles.isError ? <ErrorNotice error={articles.error} /> : null}

        {/* Der Server liefert aufsteigend -- die Reihenfolge, die der
            Ticketexport braucht. Gedreht wird in der Anzeige, damit der
            letzte Stand oben steht. */}
        {/* Dieselbe Abfolge wie am Ticket in der Agentenoberflaeche.
            ``history__change`` loeste das Listenelement auf -- Zeitstempel
            und Text standen danach als zwei gleich gewichtete Streifen da,
            ohne erkennbare Zusammengehoerigkeit. */}
        <ul className="timeline">
          {[...(articles.data ?? [])].reverse().map((article) => (
            <li key={article.id} className="timeline__entry timeline__entry--public">
              <div className="timeline__head">
                <span className="timeline__time">
                  {formatDateTime(article.created_at, i18n.language)}
                </span>
              </div>
              <p className="prose">{article.body_text}</p>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

/**
 * Maskiert Text, bevor er in HTML eingesetzt wird.
 *
 * Der Server entschaerft ohnehin -- aber er entfernt dabei, was wie ein
 * Element aussieht. Ein Kunde, der "<Fehler>" schreibt, bekaeme sonst eine
 * Rueckmeldung ohne dieses Wort und keinen Hinweis, warum.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br />');
}
